import { useCallback, useRef, useState } from 'react';
import type { TextPromptWithInputItem } from '@/app/types';
import type { QuickPrompt, SessionMode } from '../../../../types';
import { parseQuickPromptToInputValue } from '../utils/quick-prompt';

export interface UseClawInputDraftReturn {
  inputValue: TextPromptWithInputItem;
  /** 渲染期同步的 ref，避免在 callback 里读到过期闭包。 */
  inputValueRef: React.RefObject<TextPromptWithInputItem>;
  setInputValue: React.Dispatch<React.SetStateAction<TextPromptWithInputItem>>;
  /** 当前命中的 quick prompt 元信息：label 用作 session 名称，mode 仅在创建新 session 时透传。 */
  quickPromptMetaRef: React.RefObject<{ label: string; mode?: SessionMode } | null>;
  /** 切 quick prompt 时回填文本：assets 里出现过的 @<filename> 会被打成 mention。 */
  applyQuickPromptText: (item: QuickPrompt, getMentionId?: (fileName: string) => string) => void;
  /** 清空文本草稿（不动附件 / 编辑器，调用方按需要再 clear）。 */
  resetDraftText: () => void;
}

/**
 * 收敛 ChatInput 内的"输入文本草稿"状态：
 * - inputValue 主状态 + 渲染期同步 ref（与历史实现保持读时序一致）；
 * - quick prompt 切换时回填文本；
 * - reset 单独留给调用方组合（编辑器清空、附件清空走各自的 hook）。
 */
export const useClawInputDraft = (): UseClawInputDraftReturn => {
  const [inputValue, setInputValue] = useState<TextPromptWithInputItem>([]);
  const inputValueRef = useRef<TextPromptWithInputItem>(inputValue);
  // 渲染期同步：与原 ChatInput 中 `inputValueRef.current = inputValue;` 行为对齐，
  // 保证后续同步 callback 能立即读到最新值，不依赖 effect 时序。
  inputValueRef.current = inputValue;

  const quickPromptMetaRef = useRef<{ label: string; mode?: SessionMode } | null>(null);

  const applyQuickPromptText = useCallback((item: QuickPrompt, getMentionId?: (fileName: string) => string) => {
    setInputValue(parseQuickPromptToInputValue(item, getMentionId ?? ((fileName) => fileName)));
  }, []);

  const resetDraftText = useCallback(() => {
    setInputValue([]);
    quickPromptMetaRef.current = null;
  }, []);

  return {
    inputValue,
    inputValueRef,
    setInputValue,
    quickPromptMetaRef,
    applyQuickPromptText,
    resetDraftText,
  };
};
